import { useCallback, useState } from 'react';
import { Message } from '../types';

type LocalMessage = Message & { knownMessageIds?: string[] };

let nextPendingId = 0;

const isSameSentMessage = (local: LocalMessage, saved: Message): boolean => {
  if (local.id === String(saved.id)) return true;
  if (local.deliveryStatus !== 'pending' || saved.sender !== 'user') return false;
  if (local.knownMessageIds?.includes(String(saved.id))) return false;

  const localTime = new Date(local.timestamp).getTime();
  const savedTime = new Date(saved.timestamp).getTime();
  return Number.isFinite(localTime)
    && Number.isFinite(savedTime)
    && savedTime >= localTime - 5000
    && savedTime - localTime < 10 * 60 * 1000
    && (saved.content === local.content || saved.content.endsWith(local.content));
};

export const useOptimisticMessages = () => {
  const [messages, setMessages] = useState<LocalMessage[]>([]);

  const resetMessages = useCallback(() => setMessages([]), []);

  const addPendingMessage = (conversationId: string, content: string): string => {
    const id = `pending-${Date.now()}-${nextPendingId++}`;
    const timestamp = new Date().toISOString();
    setMessages((current) => [
      ...current,
      {
        id,
        conversationId,
        content,
        timestamp,
        sender: 'user',
        fromMe: true,
        deliveryStatus: 'pending',
        knownMessageIds: current.filter((message) => message.deliveryStatus !== 'pending').map((message) => String(message.id)),
      },
    ]);
    return id;
  };

  const confirmMessage = (pendingId: string, saved: Message) => {
    setMessages((current) => {
      if (current.some((message) => message.id === String(saved.id))) {
        return current.filter((message) => message.id !== pendingId);
      }
      return current.map((message) => message.id === pendingId
        ? { ...saved, id: String(saved.id), deliveryStatus: 'saved' }
        : message);
    });
  };

  const removePendingMessage = (pendingId: string) => {
    setMessages((current) => current.filter((message) => message.id !== pendingId));
  };

  const receiveMessages = useCallback((incoming: Message[]) => {
    setMessages((current) => {
      const saved = incoming.map((message) => ({ ...message, id: String(message.id) }));
      const matchedIds = new Set<string>();
      const remaining = current.filter((local) => {
        if (!local.deliveryStatus) return false;
        const match = saved.find((message) => !matchedIds.has(message.id) && isSameSentMessage(local, message));
        if (match) {
          matchedIds.add(match.id);
          return false;
        }
        return true;
      });

      for (const local of remaining) {
        const localTime = new Date(local.timestamp).getTime();
        const nextIndex = saved.findIndex((message) => new Date(message.timestamp).getTime() > localTime);
        saved.splice(nextIndex < 0 ? saved.length : nextIndex, 0, local);
      }
      return saved;
    });
  }, []);

  return { messages, resetMessages, addPendingMessage, confirmMessage, removePendingMessage, receiveMessages };
};
